import React, { useState, useRef , useEffect } from 'react';

import { useDispatch } from 'react-redux';
import { Box, Typography } from '@mui/material';

import { PKDCheckbox } from 'components/Consent/Checkbox';
import { Container } from 'shared-components/Container';
import { useUpsertPersonAttributeMutation } from 'store/api/pkd/endpoints/person';
import { openSnackbar } from 'store/application';
import {
  CheckboxSkeleton,
  CheckboxTextSkeleton,
} from 'components/SkeletonLoaders';
import {
  getAttributesPayload,
  getPersonAttribute,
} from 'pages/MedicalInformation/utils';
import { useGetPersonAndAttributes } from 'hooks/useGetPersonAndAttributes';


export const CommunicationPreferences = () => {
  const dispatch = useDispatch();
  const { person, isPersonLoading, personId, isPersonAttributesFetching } =
    useGetPersonAndAttributes();
  const [upsertPersonAttribute, { isLoading: isPersonAttributeUpserting }] =
    useUpsertPersonAttributeMutation();
  const [currentPreference, setCurrentPreference] = useState('');
  const toggleCommunication = useRef(false);

  const communicationPreferences = getPersonAttribute(person?.attributes, {
    communicationReminders        : '',
    communicationNewsletter       : '',
    communicationStudyOpportunity : '',
  });

  const savePreferences = async () => {
    toggleCommunication.current = true;
    const attributes = getPersonAttribute(
      person?.attributes,
      communicationPreferences
    );
    const body = getAttributesPayload(communicationPreferences, attributes);
    if (body.length > 0) {
      // upsert attributes
      const { error } = await upsertPersonAttribute({
        body,
        personId,
      });
      toggleCommunication.current = false;
      setCurrentPreference('');
      if (error) {
        return dispatch(
          openSnackbar({
            type : 'error',
            message :
              'Could not save your communication preference. Please try again',
          })
        );
      }
    }

    dispatch(
      openSnackbar({
        type    : 'success',
        message : 'Communication preference successfully saved',
      })
    );
  };

  const handleChange = (e) => {
    const { name, checked } = e.target;
    setCurrentPreference(name);
    for (const preference in communicationPreferences) {
      if (preference === name) {
        const isChecked = checked.toString();
        communicationPreferences[preference] = isChecked;
        savePreferences();
      }
    }
  };

  const preferencesLabels = {
    communicationReminders :
      'Module reminders (if you opt out of reminders, you will only be notified when there is a new module available)',
    communicationNewsletter       : 'Quarterly Newsletters',
    communicationStudyOpportunity : 'Clinical Study Opportunities',
  };

  const content = isPersonLoading
    ? Object.keys(preferencesLabels).map((label) => (
      <Box
        key={label}
        sx={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
        <CheckboxSkeleton />
        <CheckboxTextSkeleton />
      </Box>
    ))
    : Object.keys(communicationPreferences ?? {}).map((preference) => (
      <PKDCheckbox
        key={preference}
        name={preference}
        data-testid={`communication-${preference}-checkbox`}
        disabled={
          currentPreference === preference ||
            (isPersonAttributesFetching &&
              currentPreference === '' &&
              toggleCommunication.current)
        }
        checked={communicationPreferences?.[preference] === 'true'}
        isLoading={
          currentPreference === preference ||
            (isPersonAttributesFetching &&
              currentPreference === '' &&
              toggleCommunication.current)
        }
        onChange={handleChange}
        label={preferencesLabels[preference]}
      />
    ));

  useEffect(() => {
    if (!isPersonLoading && !isPersonAttributesFetching) {
      toggleCommunication.current = false;
      setCurrentPreference('');
    }
  }, [isPersonLoading, isPersonAttributesFetching]);

  return (
    <Container
      maxWidth={{ xl: '800px' }} mb={4}>
      <Typography
        fontFamily='Montserrat' fontWeight='700' variant='h6' mb={4}>
        Communication Preferences
      </Typography>
      {content}
    </Container>
  );
};
